<template>
  <div class="page-container">
    <div class="page-header">
      <el-button @click="$router.back()" style="margin-right: 10px;">
        <el-icon><ArrowLeft /></el-icon>
        返回
      </el-button>
      <h2>统计报表 - {{ aggregatorInfo.name }}</h2>
    </div>

    <!-- 统计概览 -->
    <el-row :gutter="20" class="stats-overview">
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-value">{{ statsData.total?.total_clicks || 0 }}</div>
            <div class="stat-label">总点击量</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-value">{{ statsData.total?.unique_visitors || 0 }}</div>
            <div class="stat-label">独立访客</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-value">{{ statsData.total?.active_deeplinks || 0 }}</div>
            <div class="stat-label">活跃DeepLink</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-value">{{ deeplinkStats.length }}</div>
            <div class="stat-label">DeepLink总数</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 时间范围选择 -->
    <el-card class="filter-card">
      <el-form :model="filterForm" inline>
        <el-form-item label="时间范围">
          <el-select v-model="filterForm.days" @change="getStatsData">
            <el-option label="最近7天" :value="7" />
            <el-option label="最近15天" :value="15" />
            <el-option label="最近30天" :value="30" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="getStatsData">
            <el-icon><Refresh /></el-icon>
            刷新
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 每日点击趋势图 -->
    <el-card class="chart-card">
      <template #header>
        <span>每日点击趋势</span>
      </template>
      <div class="chart-container">
        <v-chart :option="dailyChartOption" style="height: 300px;" />
      </div>
    </el-card>

    <!-- DeepLink统计表格 -->
    <el-card>
      <template #header>
        <span>DeepLink统计</span>
      </template>
      
      <el-table :data="deeplinkStats" v-loading="loading" stripe>
        <el-table-column prop="name" label="名称" min-width="150" />
        <el-table-column prop="url" label="链接" min-width="200" show-overflow-tooltip />
        <el-table-column prop="click_limit" label="点击限制" width="120" align="center">
          <template #default="{ row }">
            {{ row.click_limit === 0 ? '无限制' : row.click_limit }}
          </template>
        </el-table-column>
        <el-table-column prop="current_clicks" label="当前点击" width="120" align="center" />
        <el-table-column prop="total_clicks" label="总点击量" width="120" align="center" />
        <el-table-column prop="unique_visitors" label="独立访客" width="120" align="center" />
        <el-table-column prop="status" label="状态" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="使用率" width="120" align="center">
          <template #default="{ row }">
            <el-progress 
              :percentage="getUsagePercentage(row)" 
              :color="getUsageColor(row)"
              :show-text="false"
            />
            <span class="usage-text">{{ getUsagePercentage(row) }}%</span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { LineChart } from 'echarts/charts'
import { TitleComponent, TooltipComponent, LegendComponent, GridComponent } from 'echarts/components'
import VChart from 'vue-echarts'
import { aggregatorApi, redirectApi } from '../api'

// 注册ECharts组件
use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
])

export default {
  name: 'Statistics',
  components: {
    VChart
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const loading = ref(false)
    const aggregatorInfo = ref({})
    const statsData = ref({})
    const deeplinkStats = ref([])
    
    const filterForm = reactive({
      days: 7
    })
    
    // 获取聚合链接信息
    const getAggregatorInfo = async () => {
      try {
        const response = await aggregatorApi.getDetail(props.id)
        if (response.success) {
          aggregatorInfo.value = response.data
        }
      } catch (error) {
        ElMessage.error('获取聚合链接信息失败')
      }
    }
    
    // 获取统计数据
    const getStatsData = async () => {
      try {
        loading.value = true
        const response = await redirectApi.getStats(props.id, { days: filterForm.days })
        if (response.success) {
          statsData.value = response.data
          deeplinkStats.value = response.data.deeplinks || []
        }
      } catch (error) {
        ElMessage.error('获取统计数据失败')
      } finally {
        loading.value = false
      }
    }
    
    // 每日趋势图配置
    const dailyChartOption = computed(() => {
      const daily = statsData.value.daily || []
      const dates = daily.map(item => item.date).reverse()
      const clicks = daily.map(item => item.clicks).reverse()
      const visitors = daily.map(item => item.unique_visitors).reverse()
      
      return {
        title: {
          text: '每日点击趋势',
          left: 'center'
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          }
        },
        legend: {
          data: ['点击量', '独立访客'],
          top: 30
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dates
        },
        yAxis: {
          type: 'value'
        },
        series: [
          {
            name: '点击量',
            type: 'line',
            data: clicks,
            smooth: true,
            itemStyle: {
              color: '#409EFF'
            }
          },
          {
            name: '独立访客',
            type: 'line',
            data: visitors,
            smooth: true,
            itemStyle: {
              color: '#67C23A'
            }
          }
        ]
      }
    })
    
    // 获取状态类型
    const getStatusType = (status) => {
      const types = {
        active: 'success',
        inactive: 'info',
        exhausted: 'warning'
      }
      return types[status] || 'info'
    }
    
    // 获取状态文本
    const getStatusText = (status) => {
      const texts = {
        active: '启用',
        inactive: '禁用',
        exhausted: '已耗尽'
      }
      return texts[status] || '未知'
    }
    
    // 计算使用率
    const getUsagePercentage = (row) => {
      if (row.click_limit === 0) return 0
      return Math.round((row.current_clicks / row.click_limit) * 100)
    }
    
    // 获取使用率颜色
    const getUsageColor = (row) => {
      const percentage = getUsagePercentage(row)
      if (percentage >= 90) return '#F56C6C'
      if (percentage >= 70) return '#E6A23C'
      return '#67C23A'
    }
    
    onMounted(() => {
      getAggregatorInfo()
      getStatsData()
    })
    
    return {
      loading,
      aggregatorInfo,
      statsData,
      deeplinkStats,
      filterForm,
      dailyChartOption,
      getStatsData,
      getStatusType,
      getStatusText,
      getUsagePercentage,
      getUsageColor
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f2f5;
}

.page-header h2 {
  margin: 0;
  color: #303133;
  font-size: 28px;
  font-weight: 600;
}

.stats-overview {
  margin-bottom: 30px;
}

.stat-card {
  text-align: center;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px 0 rgba(0, 0, 0, 0.15);
}

.stat-content {
  padding: 30px 20px;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f2f5 100%);
}

.stat-value {
  font-size: 36px;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
  letter-spacing: 1px;
}

.stat-label {
  font-size: 16px;
  color: #606266;
  font-weight: 500;
}

.filter-card {
  margin-bottom: 30px;
  border-radius: 16px;
  overflow: hidden;
}

.chart-card {
  margin-bottom: 30px;
  border-radius: 16px;
  overflow: hidden;
}

.chart-container {
  width: 100%;
  padding: 20px;
}

.usage-text {
  font-size: 14px;
  color: #606266;
  margin-left: 8px;
  font-weight: 500;
}

/* 表格样式优化 */
:deep(.el-table) {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.08);
}

:deep(.el-table__header) {
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f2f5 100%);
}

:deep(.el-table th) {
  background: transparent;
  color: #606266;
  font-weight: 600;
  border-bottom: 2px solid #e4e7ed;
}

:deep(.el-table td) {
  border-bottom: 1px solid #f0f2f5;
}

:deep(.el-table tr:hover > td) {
  background-color: #f8f9ff;
}

/* 进度条样式优化 */
:deep(.el-progress-bar__outer) {
  border-radius: 8px;
  background-color: #f0f2f5;
}

:deep(.el-progress-bar__inner) {
  border-radius: 8px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-header h2 {
    font-size: 24px;
  }
  
  .stat-value {
    font-size: 28px;
  }
  
  .stat-label {
    font-size: 14px;
  }
  
  .stat-content {
    padding: 20px 15px;
  }
  
  .chart-container {
    padding: 10px;
  }
}
</style>
